/**
 * FormFields.tsx — wiederverwendbare Formular-Bausteine für die Wizard-Steps.
 *
 * Alle arbeiten auf String-Werten (passend zum Store-`fields:Record<string,string>`).
 * Editorial-Stil konsistent mit SingleTextareaStep / StammdatenStep.
 */
import type { ReactNode } from 'react';
import { FieldHelp } from '../../forms/FieldHelp';
import type { FieldHelpKey } from '../../../lib/i18n/fieldHelp';

interface ShellProps {
  readonly id?: string | undefined;
  readonly label: string;
  readonly hint?: string | undefined;
  readonly required?: boolean | undefined;
  readonly helpKey?: FieldHelpKey | undefined;
  readonly children: ReactNode;
}

function FieldShell({ id, label, hint, required, helpKey, children }: ShellProps) {
  return (
    <div>
      <label htmlFor={id} className="flex items-start gap-2">
        <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {label}
          {required && (
            <span className="ml-2 normal-case tracking-normal text-stone-500">· Pflicht</span>
          )}
        </span>
        {helpKey && <FieldHelp pathKey={helpKey} className="-mt-1.5" />}
      </label>
      {hint && <p className="mt-2 font-serif text-sm italic text-stone-500">{hint}</p>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

/**
 * Shell für gruppierte Controls (Radio-Paar, Checkbox-Liste). Diese haben
 * kein einzelnes fokussierbares `id`-Ziel, deshalb wäre ein `<label htmlFor>`
 * verwaist. Stattdessen `<fieldset>`/`<legend>` — semantisch korrekt für
 * Steuerelement-Gruppen (WCAG 1.3.1 / 4.1.2). Die Legend trägt die bisherigen
 * Label-Klassen, damit der Editorial-Stil identisch bleibt.
 */
function FieldsetShell({ label, hint, required, helpKey, children }: Omit<ShellProps, 'id'>) {
  return (
    <fieldset className="min-w-0">
      <legend className="flex items-start gap-2 p-0">
        <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {label}
          {required && (
            <span className="ml-2 normal-case tracking-normal text-stone-500">· Pflicht</span>
          )}
        </span>
        {helpKey && <FieldHelp pathKey={helpKey} className="-mt-1.5" />}
      </legend>
      {hint && <p className="mt-2 font-serif text-sm italic text-stone-500">{hint}</p>}
      <div className="mt-3">{children}</div>
    </fieldset>
  );
}

const INPUT_CLASS =
  'w-full rounded-sm border border-stone-200 bg-white px-4 py-3 font-serif text-base text-stone-900 placeholder-stone-500 focus:border-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2';

interface BaseFieldProps {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly required?: boolean;
  readonly helpKey?: FieldHelpKey;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
}

export function TextField(p: BaseFieldProps) {
  return (
    <FieldShell id={p.id} label={p.label} hint={p.hint} required={p.required} helpKey={p.helpKey}>
      <input
        id={p.id}
        type="text"
        value={p.value}
        placeholder={p.placeholder}
        aria-required={p.required ? true : undefined}
        onChange={(e) => {
          p.onChange(e.target.value);
        }}
        className={INPUT_CLASS}
      />
    </FieldShell>
  );
}

export function TextareaField(
  p: BaseFieldProps & { readonly rows?: number; readonly minLength?: number },
) {
  const len = p.value.trim().length;
  return (
    <FieldShell id={p.id} label={p.label} hint={p.hint} required={p.required} helpKey={p.helpKey}>
      <textarea
        id={p.id}
        value={p.value}
        rows={p.rows ?? 4}
        placeholder={p.placeholder}
        aria-required={p.required ? true : undefined}
        onChange={(e) => {
          p.onChange(e.target.value);
        }}
        className={`${INPUT_CLASS} resize-y leading-relaxed`}
      />
      {p.minLength !== undefined && len > 0 && len < p.minLength && (
        <p className="mt-1 font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          Mind. {p.minLength} Zeichen ({len})
        </p>
      )}
    </FieldShell>
  );
}

export function NumberField(
  p: BaseFieldProps & { readonly suffix?: string; readonly min?: number; readonly max?: number },
) {
  return (
    <FieldShell id={p.id} label={p.label} hint={p.hint} required={p.required} helpKey={p.helpKey}>
      <div className="flex items-center gap-3">
        <input
          id={p.id}
          type="number"
          inputMode="decimal"
          min={p.min}
          max={p.max}
          value={p.value}
          placeholder={p.placeholder}
          aria-required={p.required ? true : undefined}
          onChange={(e) => {
            p.onChange(e.target.value);
          }}
          className={`${INPUT_CLASS} max-w-[12rem] tabular-nums`}
        />
        {p.suffix && (
          <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {p.suffix}
          </span>
        )}
      </div>
    </FieldShell>
  );
}

export interface Option {
  readonly value: string;
  readonly label: string;
}

export function SelectField(p: BaseFieldProps & { readonly options: readonly Option[] }) {
  return (
    <FieldShell id={p.id} label={p.label} hint={p.hint} required={p.required} helpKey={p.helpKey}>
      <select
        id={p.id}
        value={p.value}
        aria-required={p.required ? true : undefined}
        onChange={(e) => {
          p.onChange(e.target.value);
        }}
        className={`${INPUT_CLASS} max-w-md`}
      >
        <option value="">{p.placeholder ?? '— bitte wählen —'}</option>
        {p.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

/** Ja/Nein als Radio-Paar. value = 'true'|'false'|'' (unbeantwortet). */
export function BooleanField(p: {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly required?: boolean;
  readonly helpKey?: FieldHelpKey;
  readonly value: string;
  readonly onChange: (value: 'true' | 'false') => void;
  readonly yesLabel?: string;
  readonly noLabel?: string;
}) {
  const opt = (val: 'true' | 'false', text: string) => {
    const active = p.value === val;
    return (
      <button
        type="button"
        aria-pressed={active}
        onClick={() => {
          p.onChange(val);
        }}
        className={`inline-flex min-h-[44px] items-center rounded-sm border px-5 py-2.5 font-sans text-xs font-medium uppercase tracking-widest transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2 ${
          active
            ? 'border-stone-900 bg-stone-900 text-white'
            : 'border-stone-200 bg-white text-stone-600 hover:border-stone-400'
        }`}
      >
        {text}
      </button>
    );
  };
  return (
    <FieldsetShell label={p.label} hint={p.hint} required={p.required} helpKey={p.helpKey}>
      <div className="flex gap-3">
        {opt('true', p.yesLabel ?? 'Ja')}
        {opt('false', p.noLabel ?? 'Nein')}
      </div>
    </FieldsetShell>
  );
}

/** Mehrfachauswahl als Checkbox-Liste; arbeitet auf string[]. */
export function CheckboxGroup(p: {
  readonly label: string;
  readonly hint?: string;
  readonly helpKey?: FieldHelpKey;
  readonly options: readonly Option[];
  readonly selected: readonly string[];
  readonly onToggle: (value: string, next: boolean) => void;
}) {
  return (
    <FieldsetShell label={p.label} hint={p.hint} helpKey={p.helpKey}>
      <div className="flex flex-wrap gap-2">
        {p.options.map((o) => {
          const active = p.selected.includes(o.value);
          return (
            <label
              key={o.value}
              className={`inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-sm border px-3 py-2 font-serif text-sm transition-colors ${
                active
                  ? 'border-stone-900 bg-stone-50 text-stone-900'
                  : 'border-stone-200 text-stone-600 hover:border-stone-400'
              }`}
            >
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => {
                  p.onToggle(o.value, e.target.checked);
                }}
                className="h-4 w-4 accent-stone-900"
              />
              {o.label}
            </label>
          );
        })}
      </div>
    </FieldsetShell>
  );
}
